import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import BottomNav from "@/components/BottomNav";
import Header from "@/components/Header";
import StatusBadge from "@/components/StatusBadge";
import { 
  Castle, 
  PartyPopper, 
  User, 
  Phone, 
  Mail, 
  MapPin, 
  Users, 
  Calendar as CalendarIcon,
  Clock,
  Euro,
  FileText,
  CheckCircle,
  XCircle,
  Ban,
  Edit,
  Pencil,
  Trash2,
  MessageCircle
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  { id: "matin", name: "Matin", hours: "8h - 13h" },
  { id: "apres-midi", name: "Après-midi", hours: "14h - 19h" },
  { id: "journee", name: "Journée complète", hours: "8h - 19h" },
];

const SOURCES = [
  { id: "messenger", name: "Messenger" },
  { id: "telephone", name: "Téléphone" },
  { id: "manuel", name: "Manuel" },
  { id: "site_web", name: "Site web" },
  { id: "chatbot", name: "Chatbot" },
];

export default function ReservationDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  
  // Catalogue data
  const [structures, setStructures] = useState([]);
  const [offers, setOffers] = useState([]);
  const [options, setOptions] = useState([]);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [resResponse, catalogueResponse] = await Promise.all([
        fetch(`${API}/reservations/${id}`, { credentials: "include" }),
        fetch(`${API}/catalogue`, { credentials: "include" }),
      ]);
      
      if (!resResponse.ok) throw new Error("Not found");
      
      setReservation(await resResponse.json());
      
      if (catalogueResponse.ok) {
        const catalogue = await catalogueResponse.json();
        setStructures(catalogue.structures);
        setOffers(catalogue.offers);
        setOptions(catalogue.options);
      }
    } catch (error) {
      toast.error("Réservation introuvable");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action) => {
    setActionLoading(action);
    try {
      const response = await fetch(`${API}/reservations/${id}/${action}`, {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }
      setReservation(await response.json());
      
      const messages = {
        confirm: "Réservation confirmée",
        cancel: "Réservation annulée",
        block: "Créneau bloqué",
      };
      toast.success(messages[action]);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API}/reservations/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }
      toast.success("Réservation supprimée");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSaveEdit = async (editedData) => {
    try {
      const response = await fetch(`${API}/reservations/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(editedData),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }
      
      setReservation(await response.json());
      setEditModalOpen(false);
      toast.success("Réservation modifiée");
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen festive-gradient flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-festive-pink border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!reservation) return null;

  const structure = structures.find(s => s.id === reservation.structure_id) || { name: reservation.structure_id };
  const offer = offers.find(o => o.id === reservation.offer_id) || { name: reservation.offer_id };
  const timeSlot = TIME_SLOTS.find(t => t.id === reservation.time_slot) || { name: reservation.time_slot };

  const formatDate = (dateStr) => {
    return format(new Date(dateStr), "EEEE d MMMM yyyy", { locale: fr });
  };

  return (
    <div className="min-h-screen festive-gradient confetti-pattern page-container">
      <Header title="Détails réservation" showBack />
      
      <main className="p-4 space-y-4 max-w-2xl mx-auto">
        {/* Status & Actions Header */}
        <Card className="card-festive overflow-hidden">
          <div className={`p-4 ${
            reservation.status === "DEMANDE_EN_LIGNE" ? "bg-cyan-50" :
            reservation.status === "OPTION" ? "bg-yellow-50" :
            reservation.status === "CONFIRME" ? "bg-green-50" :
            reservation.status === "ANNULE" ? "bg-red-50" :
            "bg-gray-50"
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Statut actuel</p>
                <StatusBadge status={reservation.status} large />
              </div>
              <div className="flex items-center gap-2">
                {/* Edit Button */}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setEditModalOpen(true)}
                  className="rounded-xl border-2 border-festive-pink text-festive-pink hover:bg-pink-50"
                  data-testid="edit-reservation-btn"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <div className="text-right">
                  <p className="text-sm text-gray-500 mb-1">Référence</p>
                  <p className="font-mono text-sm font-semibold">{reservation.id}</p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Client Info */}
        <Card className="card-festive">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg font-heading">
              <User className="w-5 h-5 text-festive-pink" />
              Informations client
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-festive-pink/20 to-festive-purple/20 flex items-center justify-center">
                <User className="w-6 h-6 text-festive-pink" />
              </div>
              <div>
                <p className="font-semibold text-lg">{reservation.client_name}</p>
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <a
                href={`tel:${reservation.client_phone}`}
                className="flex items-center gap-3 p-3 rounded-xl bg-pink-50 hover:bg-pink-100 transition-colors"
                data-testid="call-client-btn"
              >
                <Phone className="w-5 h-5 text-festive-pink" />
                <span className="font-semibold text-festive-pink">{reservation.client_phone}</span>
              </a>

              <a
                href={`mailto:${reservation.client_email}`}
                className="flex items-center gap-3 p-3 rounded-xl bg-purple-50 hover:bg-purple-100 transition-colors"
              >
                <Mail className="w-5 h-5 text-festive-purple" />
                <span className="text-gray-700">{reservation.client_email}</span>
              </a>

              <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50">
                <MapPin className="w-5 h-5 text-gray-500 mt-0.5" />
                <span className="text-gray-700">{reservation.client_address}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reservation Details */}
        <Card className="card-festive">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg font-heading">
              <Castle className="w-5 h-5 text-festive-purple" />
              Détails de la réservation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <InfoRow
              icon={<Castle className="w-5 h-5" />}
              label="Structure"
              value={structure.name}
              subValue={structure.dimensions}
            />
            <InfoRow
              icon={<PartyPopper className="w-5 h-5" />}
              label="Offre"
              value={offer.name}
              badge={offer.requires_validation ? "Validation requise" : null}
            />
            <InfoRow
              icon={<CalendarIcon className="w-5 h-5" />}
              label="Date"
              value={formatDate(reservation.date)}
            />
            <InfoRow
              icon={<Clock className="w-5 h-5" />}
              label="Créneau"
              value={timeSlot.name}
              subValue={timeSlot.hours}
            />
            <InfoRow
              icon={<MessageCircle className="w-5 h-5" />}
              label="Source"
              value={SOURCES.find(s => s.id === reservation.source)?.name || reservation.source}
            />
          </CardContent>
        </Card>

        {/* Options */}
        {((Array.isArray(reservation.options) && reservation.options.length > 0) || reservation.needs_generator) && (
          <Card className="card-festive">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg font-heading">
                <PartyPopper className="w-5 h-5 text-festive-yellow" />
                Options
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Array.isArray(reservation.options) && reservation.options.map((optId) => {
                  const opt = options.find(o => o.id === optId);
                  return (
                    <Badge
                      key={optId}
                      variant="outline"
                      className="px-3 py-1 border-festive-yellow bg-yellow-50 text-yellow-700"
                    >
                      {opt?.name || optId}
                    </Badge>
                  );
                })}
                {reservation.needs_generator && (
                  <Badge
                    variant="outline"
                    className="px-3 py-1 border-festive-cyan bg-cyan-50 text-cyan-700"
                  >
                    Groupe électrogène
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notes */}
        {reservation.notes && (
          <Card className="card-festive">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg font-heading">
                <FileText className="w-5 h-5 text-gray-500" />
                Notes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 whitespace-pre-wrap">{reservation.notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Price */}
        <Card className="card-festive bg-gradient-to-br from-festive-pink/10 to-festive-purple/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Euro className="w-6 h-6 text-festive-pink" />
                <span className="text-lg font-medium">Total</span>
              </div>
              <span className="text-3xl font-heading font-bold text-festive-pink">
                {reservation.total_price}€
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="space-y-3 pt-2">
          {(reservation.status === "OPTION" || reservation.status === "DEMANDE_EN_LIGNE") && (
            <>
              <Button
                data-testid="confirm-btn"
                onClick={() => handleAction("confirm")}
                disabled={actionLoading === "confirm"}
                className="w-full h-14 bg-green-500 hover:bg-green-600 text-white font-semibold text-lg rounded-2xl"
              >
                {actionLoading === "confirm" ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="w-6 h-6 mr-2" />
                    Confirmer la réservation
                  </>
                )}
              </Button>

              <Button
                data-testid="cancel-btn"
                onClick={() => handleAction("cancel")}
                disabled={actionLoading === "cancel"}
                variant="outline"
                className="w-full h-14 border-2 border-red-300 text-red-600 hover:bg-red-50 font-semibold text-lg rounded-2xl"
              >
                {actionLoading === "cancel" ? (
                  <div className="w-5 h-5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <XCircle className="w-6 h-6 mr-2" />
                    Annuler
                  </>
                )}
              </Button>

              <Button
                data-testid="block-btn"
                onClick={() => handleAction("block")}
                disabled={actionLoading === "block"}
                variant="outline"
                className="w-full h-14 border-2 border-gray-300 text-gray-600 hover:bg-gray-50 font-semibold text-lg rounded-2xl"
              >
                {actionLoading === "block" ? (
                  <div className="w-5 h-5 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Ban className="w-6 h-6 mr-2" />
                    Bloquer (maintenance)
                  </>
                )}
              </Button>
            </>
          )}

          {reservation.status === "CONFIRME" && (
            <Button
              data-testid="cancel-btn"
              onClick={() => handleAction("cancel")}
              disabled={actionLoading === "cancel"}
              variant="outline"
              className="w-full h-14 border-2 border-red-300 text-red-600 hover:bg-red-50 font-semibold text-lg rounded-2xl"
            >
              {actionLoading === "cancel" ? (
                <div className="w-5 h-5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <XCircle className="w-6 h-6 mr-2" />
                  Annuler la réservation
                </>
              )}
            </Button>
          )}

          {reservation.status === "ANNULE" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  data-testid="delete-btn"
                  variant="outline"
                  className="w-full h-14 border-2 border-red-300 text-red-600 hover:bg-red-50 font-semibold text-lg rounded-2xl"
                >
                  <Trash2 className="w-6 h-6 mr-2" />
                  Supprimer définitivement
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Supprimer la réservation ?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Cette action est irréversible. La réservation sera définitivement supprimée.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-red-500 hover:bg-red-600"
                  >
                    Supprimer
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </main>

      {/* Edit Modal */}
      <EditReservationModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        reservation={reservation}
        structures={structures}
        offers={offers}
        options={options}
        onSave={handleSaveEdit}
      />

      <BottomNav />
    </div>
  );
}

const InfoRow = ({ icon, label, value, subValue, badge }) => (
  <div className="flex items-start gap-3">
    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-festive-pink/10 to-festive-purple/10 flex items-center justify-center text-festive-pink flex-shrink-0">
      {icon}
    </div>
    <div className="flex-1">
      <p className="text-sm text-gray-500">{label}</p>
      <div className="flex items-center gap-2">
        <p className="font-semibold">{value}</p>
        {badge && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
            {badge}
          </span>
        )}
      </div>
      {subValue && <p className="text-sm text-gray-500">{subValue}</p>}
    </div>
  </div>
);

// Edit Reservation Modal Component
const EditReservationModal = ({ open, onClose, reservation, structures, offers, options, onSave }) => {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    if (reservation) {
      setForm({
        structure_id: reservation.structure_id,
        offer_id: reservation.offer_id,
        date: reservation.date,
        time_slot: reservation.time_slot,
        client_name: reservation.client_name,
        client_phone: reservation.client_phone,
        client_email: reservation.client_email,
        client_address: reservation.client_address,
        options: Array.isArray(reservation.options) ? reservation.options : [],
        needs_generator: reservation.needs_generator,
        source: reservation.source,
        notes: reservation.notes || "",
      });
    }
  }, [reservation, open]);

  const toggleOption = (optionId) => {
    setForm(prev => ({
      ...prev,
      options: prev.options.includes(optionId)
        ? prev.options.filter(id => id !== optionId)
        : [...prev.options, optionId],
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    await onSave(form);
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-5 h-5 text-festive-pink" />
            Modifier la réservation
          </DialogTitle>
          <DialogDescription>
            Modifiez les informations de la réservation
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Structure & Offer */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Structure</Label>
              <Select value={form.structure_id} onValueChange={(v) => setForm({ ...form, structure_id: v })}>
                <SelectTrigger className="input-festive mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {structures.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Offre</Label>
              <Select value={form.offer_id} onValueChange={(v) => setForm({ ...form, offer_id: v })}>
                <SelectTrigger className="input-festive mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {offers.map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.name} - {o.price}€</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Date</Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full input-festive mt-1 justify-start">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {form.date ? format(new Date(form.date), "d MMMM yyyy", { locale: fr }) : "Sélectionner"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={form.date ? new Date(form.date) : undefined}
                    onSelect={(date) => {
                      if (date) {
                        setForm({ ...form, date: format(date, "yyyy-MM-dd") });
                        setCalendarOpen(false);
                      }
                    }}
                    locale={fr}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label>Créneau</Label>
              <Select value={form.time_slot} onValueChange={(v) => setForm({ ...form, time_slot: v })}>
                <SelectTrigger className="input-festive mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name} ({t.hours})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Client Info */}
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-700">Informations client</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nom complet</Label>
                <Input
                  value={form.client_name || ""}
                  onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                  className="input-festive mt-1"
                />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={form.client_phone || ""}
                  onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                  className="input-festive mt-1"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Email</Label>
                <Input
                  value={form.client_email || ""}
                  onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                  className="input-festive mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Adresse</Label>
              <Textarea
                value={form.client_address || ""}
                onChange={(e) => setForm({ ...form, client_address: e.target.value })}
                className="input-festive mt-1"
              />
            </div>
          </div>

          {/* Options */}
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-700">Options</h4>
            <div className="grid grid-cols-2 gap-2">
              {options.map(option => (
                <Label
                  key={option.id}
                  className={`flex items-center gap-2 p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                    form.options?.includes(option.id)
                      ? "border-festive-yellow bg-yellow-50"
                      : "border-gray-100 hover:border-yellow-200"
                  }`}
                >
                  <Checkbox
                    checked={form.options?.includes(option.id)}
                    onCheckedChange={() => toggleOption(option.id)}
                  />
                  <span className="text-sm">{option.name}</span>
                  <span className="text-sm font-semibold text-festive-pink ml-auto">{option.price}€</span>
                </Label>
              ))}
            </div>
            <Label
              className={`flex items-center gap-2 p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                form.needs_generator
                  ? "border-festive-cyan bg-cyan-50"
                  : "border-gray-100 hover:border-cyan-200"
              }`}
            >
              <Checkbox
                checked={form.needs_generator}
                onCheckedChange={(checked) => setForm({ ...form, needs_generator: checked })}
              />
              <span className="text-sm">Groupe électrogène</span>
              <span className="text-sm font-semibold text-festive-pink ml-auto">50€</span>
            </Label>
          </div>

          {/* Source & Notes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Source</Label>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger className="input-festive mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOURCES.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="input-festive mt-1"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-gradient-to-r from-festive-pink to-festive-purple text-white"
          >
            {loading ? "Enregistrement..." : "Enregistrer les modifications"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
